import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';
import { Delivery } from '@app/common-codes/models/matwins-tenant/Delivery.model';
import { DataService } from '@app/common-codes/services/DataService';
import { DependencyInjectionProvider } from '@app/common-codes/services/DependecyInjection.provider';
import { LookupService, LookupItem } from '@app/common-codes/services/LookupService';
import { CurrentTenantService } from '@app/common-codes/services/CurrentTenantService';
import { AlertModalService, ToastType } from '@app/common-codes/services/AlertModalService';
import { MultiSelectComponent } from '@app/common-codes/components/nested-components/multi-select/multi-select.component';
import { MatwinsSkins } from '@app/common-codes/skins/global.skin';
import { IStaticStylesConfig } from '@app/common-codes/skins/_primitives/IStaticStylesConfig';
import { PhotoCaptureComponent } from '../../photo/photo-capture.component';
import { CheckinShellComponent } from '../checkin-shell/checkin-shell.component';

@Component({
    selector: 'app-delivery-checkin',
    templateUrl: './delivery-checkin.component.html',
    standalone: true,
    imports: [TranslatePipe, PhotoCaptureComponent, MultiSelectComponent, CheckinShellComponent]
})
export class DeliveryCheckinComponent implements OnInit, AfterViewInit {
    readonly Skin: IStaticStylesConfig = MatwinsSkins.Ipad1;

    currentStep = signal(1);
    delivery = new Delivery();
    selectedDeliveryType = signal<number>(1);
    selectedEmployeeKeys = signal<string[]>([]);
    employeeLookup = signal<LookupItem[]>([]);
    imageFile = signal<File | undefined>(undefined);
    isSaving = signal(false);

    canNext = computed(() => {
        const step = this.currentStep();
        if (step === 1) return this.selectedEmployeeKeys().length > 0 && !!this.selectedDeliveryType();
        return !!this.imageFile() && !this.isSaving();
    });

    isLastStep = computed(() => this.currentStep() === 2);

    private deliveryService?: DataService<Delivery>;
    private readonly cdr = inject(ChangeDetectorRef);

    @ViewChild(MultiSelectComponent) private employeeSelect?: MultiSelectComponent;

    constructor(
        private router: Router,
        private di: DependencyInjectionProvider,
        private lookupService: LookupService,
        private tenantService: CurrentTenantService,
        private alertModalService: AlertModalService
    ) {
        const posId = this.tenantService.currentPointOfService()?.id;
        if (posId) this.delivery.fK_PointOfService = posId;
    }

    async ngOnInit(): Promise<void> {
        this.deliveryService = this.di.getOrCreateDataService(Delivery);
        await this.deliveryService.getLookupsByNames(['Employee']);
        this.employeeLookup.set(this.lookupService.getLookupData('Employee'));
    }

    ngAfterViewInit(): void {
        setTimeout(() => this.focusFirstField(), 0);
    }

    /**
     * Focus sur le premier champ de l'étape, appelé de façon SYNCHRONE depuis
     * les handlers de navigation : sur iPad, le clavier ne s'ouvre que si
     * focus() se produit dans le contexte du geste utilisateur.
     */
    private focusFirstField(): void {
        this.cdr.detectChanges();
        if (this.currentStep() === 1 && !this.employeeSelect?.selectedKeys?.length) {
            this.employeeSelect?.openDropdown();
        }
    }

    onEmployeeSelectChange(keys: string[]): void {
        this.selectedEmployeeKeys.set(keys ?? []);
        const key = keys?.[0];
        const emp = this.employeeLookup().find(e => e.key === key);
        this.delivery.fK_Employee = key ? Number(key) : undefined;
        this.delivery.employeeName = emp?.value ?? '';
    }

    next(): void {
        if (this.currentStep() < 2) {
            this.currentStep.update(s => s + 1);
            this.focusFirstField();
        }
    }

    prev(): void {
        if (this.currentStep() > 1) {
            this.currentStep.update(s => s - 1);
            this.focusFirstField();
        }
        else this.router.navigate(['/ipad/visitor-type']);
    }

    goHome(): void {
        this.router.navigate(['/ipad/home']);
    }

    onPhotoFile(file: File): void {
        this.imageFile.set(file);
    }

    async save(): Promise<void> {
        if (!this.deliveryService || this.isSaving()) return;
        this.isSaving.set(true);
        try {
            this.delivery.deliveryDate = new Date();
            this.delivery.deliveryType = this.selectedDeliveryType();
            this.delivery.status = 1; // Available

            const saved = await this.deliveryService.create(this.delivery);

            const photo = this.imageFile();
            if (photo && saved?.id) {
                const formData = new FormData();
                formData.append('entityId', String(saved.id));
                formData.append('relationName', 'FK_Image_Package');
                formData.append('file', photo);
                await this.deliveryService.execute('POST', 'upload', formData);
            }

            // La notification part de DeliveryNotificationHandler a la creation : aucun appel a faire ici.
            this.alertModalService.showToast('Delivery saved and notified', ToastType.Success);
            this.router.navigate(['/ipad/home']);
        } catch (err) {
            console.error('Delivery save failed:', err);
            this.alertModalService.showToast('Failed to save delivery', ToastType.Error);
        } finally {
            this.isSaving.set(false);
        }
    }
}
